/** @jsxImportSource @emotion/react */
import { Cycle } from 'grommet-icons'
import { keyframes } from '@emotion/react'
import { CSSObject } from 'styled-components'
import { useRef, useEffect, useMemo } from 'react'

import angleToColor from 'util/angleToColor'
import { INITIAL_DIAL_ANGLE, useProvider } from 'hooks/useMoodPlot'
import paintColorWheelToCanvas from 'util/paintColorWheelToCanvas'

import Rotator from './Rotator'
import Knob from './styles/Knob'
import RenderEmojis from './RenderEmoji'
import * as styles from './styles/index'
import PaletteCore from './styles/PaletteCore'
import PaletteCoreCenter from './styles/PaletteCoreCenter'
import KnobAnimatedArrows from './KnobAnimatedArrows'

const spin = keyframes({
  from: { transform: 'rotate(0deg)' },
  to: { transform: 'rotate(360deg)' },
})

const shakeRotate = keyframes({
  '0%': { transform: 'rotate(0deg)' },
  '28%': { transform: 'rotate(3.5deg)' },
  '43%': { transform: 'rotate(0deg)' },
  '57%': { transform: 'rotate(0deg)' },
  '72%': { transform: 'rotate(-3.5deg)' },
  '100%': { transform: 'rotate(0deg)' },
})

const idleKnobRotatorStyles = {
  animation: `${shakeRotate} 6s infinite 4s cubic-bezier(0.65, 0, 0.35, 1)`,
}

const DIAL_DIMENSIONS = 360

type Props = {
  size?: number
  hideEmojis?: boolean
  stripeWidthPercentage?: number
}
export default function EmotionDial({
  size = DIAL_DIMENSIONS,
  hideEmojis = false,
  stripeWidthPercentage = 20,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const sizes = useMemo<CSSObject>(() => ({ height: `${size}px`, width: `${size}px` }), [size])
  const {
    state: { angle },
  } = useProvider()

  useEffect(() => {
    if (canvasRef && canvasRef.current) {
      paintColorWheelToCanvas(canvasRef.current, size)
    }
  }, [size])

  return (
    <div css={[styles.emotionDial, sizes]}>
      <canvas ref={canvasRef} width={size} height={size} css={[styles.colorCanvas]} />
      {!hideEmojis && <RenderEmojis stripeWidthPercentage={stripeWidthPercentage} />}
      <div css={styles.outerShadow} />
      <Rotator>
        <div css={[styles.rotator, angle === null && idleKnobRotatorStyles]}>
          <Knob stripeWidthPercentage={stripeWidthPercentage}>
            {angle === null && <KnobAnimatedArrows />}
          </Knob>
        </div>
      </Rotator>
      <PaletteCore stripeWidthPercentage={stripeWidthPercentage}>
        {angle !== null ? (
          <PaletteCoreCenter color={angleToColor(angle ?? INITIAL_DIAL_ANGLE)} />
        ) : (
          <PaletteCoreCenter>
            <Cycle
              size="xlarge"
              opacity={0.55}
              css={{
                transformOrigin: '50% 46.5%',
                // Easing from https://easings.net/#easeInOutCubic
                animation: `${spin} 2.5s cubic-bezier(0.65, 0, 0.35, 1) 1s 2`,
              }}
            />
          </PaletteCoreCenter>
        )}
      </PaletteCore>
    </div>
  )
}
